import { Stack, Typography } from '@mui/material';
import { enqueErrorNotification, IdentityDocumentPhotoGuide } from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { useEffect, useRef } from 'react';
import { useFileUploaderContext } from './FileUploaderContext';
import ManualEntryTrigger from './ManualEntryTrigger';
import { usePhotoGuideContext } from './PhotoGuideContext';
import { useScanContext } from './ScanContext';
import { useScrollTargetContext } from './ScrollTargetContext';
import { entityDocumentApi } from './utils';
import { useWizardContext } from './WizardContext';

export default function PhotoGuideView({ currentStep }: Readonly<{ currentStep: number }>) {
  const {
    state: { customerId },
    getSelectedDocumentTypeForStep,
  } = useWizardContext();

  const {
    state: { scanStatus },
  } = useScanContext();

  const {
    state: { fileIds },
    setFileIds,
    updateFileMutationId,
  } = useFileUploaderContext();

  const {
    state: { isShowCompactPhotoGuidance },
    setIsShowCompactPhotoGuidance,
  } = usePhotoGuideContext();

  const { registerRef, scrollToTarget } = useScrollTargetContext();

  const photoGuideRef = useRef<HTMLDivElement>(null);

  // Register the ref with ScrollTargetContext
  useEffect(() => {
    registerRef('photo-guide', photoGuideRef);
  }, [registerRef]);

  const selectedDocumentType = getSelectedDocumentTypeForStep(currentStep);

  const handleUploadAgain = async () => {
    if (!fileIds) {
      return;
    }
    try {
      for (const fileId of fileIds) {
        await entityDocumentApi.delete(fileId, customerId, selectedDocumentType);
      }
      setFileIds([]);
      setIsShowCompactPhotoGuidance(true);
      updateFileMutationId();
      // Scroll to uploader after state updates
      scrollToTarget('select-document-type');
    } catch (error) {
      console.error('Error deleting files:', error);
      enqueErrorNotification('Failed to delete files. Please try again.');
    }
  };

  if (scanStatus !== 'FAILED') {
    return null;
  }

  return (
    <Stack gap={tokens.spacingXl} ref={photoGuideRef} data-component="PhotoGuideView">
      <IdentityDocumentPhotoGuide
        onUploadAgain={handleUploadAgain}
        compact={isShowCompactPhotoGuidance}
      />
      {!isShowCompactPhotoGuidance && (
        <Stack justifyContent="center" alignItems="center" gap={tokens.spacingBase}>
          {/* TODO: Change to tokens once we have a design token for this */}
          <Typography variant="body1" sx={{ lineHeight: '150%', letterSpacing: '0.15px' }}>
            {' '}
            OR{' '}
          </Typography>
          <ManualEntryTrigger variant="link" resetFormData={true}>
            I want to manually enter my information
          </ManualEntryTrigger>
        </Stack>
      )}
    </Stack>
  );
}
